import { defineStore } from 'pinia';
import { Dialog } from 'quasar';
import { getSocket } from 'src/boot/echoSocket';
import { showErrorNotification, showSuccessNotification, showWarningNotification } from 'src/functions/function-show-notifications';
import { axiosApi } from 'src/utils/axios';
import { computed, defineAsyncComponent, ref } from 'vue';

import { useUpdateStore } from './useUpdateStore';
import { useUserStore } from './useUserStore';

export interface PresenceClient {
  session_key: string;
  app_version: string | null;
  platform: string | null;
  current_page: string | null;
  last_seen_at: string;
}

export interface PresenceUser {
  id: number;
  nom: string;
  photo: string | null;
  sessions: PresenceClient[];
}

interface FleetResponse {
  server_version: string | null;
  stale_after: number;
  users: PresenceUser[];
}

export type ClientCommand = 'check' | 'refresh-data' | 'update' | 'logout' | 'reset';

/**
 * `useUserStore` is a legacy options store in plain JS, so TypeScript infers
 * nothing from it. Naming the members used here is the same shape trick
 * `useUserPunch` already applies, and keeps the reactive proxy intact.
 */
interface UserStoreShape {
  details?: { id?: number };
  isDirecteur: boolean;
  regenUserData: (notify?: boolean) => Promise<void>;
  logout: (payload?: boolean) => void;
}

// The panel reads seconds, so it needs a clock that ticks like one. Everywhere
// else the only job is retiring sessions that stopped checking in, which a
// coarse tick does just as well for a fraction of the renders.
const CLOCK_OPEN_MS = 1000;
const CLOCK_IDLE_MS = 15000;

/**
 * Broadcasts carry news only — a client parked on one page beats silently, on
 * purpose, so the whole company does not cross the websocket every 45 seconds.
 * That leaves a watcher's copy of `last_seen_at` frozen at the last thing that
 * person actually did, and the local freshness filter then retires somebody who
 * never went anywhere. Reconciling against the server on a timer is what closes
 * that gap: both intervals sit well inside the 90s window, so a live session
 * cannot age out between polls, and a client that died without a `leave` is
 * gone within one.
 */
const RECONCILE_OPEN_MS = 20000;
const RECONCILE_IDLE_MS = 60000;

export const useOnlineStore = defineStore('Online', () => {
  const UserStore = useUserStore() as unknown as UserStoreShape;
  const UpdateStore = useUpdateStore();

  const users = ref<PresenceUser[]>([]);
  const serverVersion = ref<string | null>(null);
  const staleAfter = ref(90);
  const loading = ref(false);
  const loadFailed = ref(false);
  const dialogOpen = ref(false);

  // Freshness is a function of time, so something has to make time reactive or
  // a list left open keeps showing people who stopped checking in.
  const now = ref(Date.now());
  let clock: ReturnType<typeof setInterval> | null = null;
  let clockRate = 0;
  let reconciler: ReturnType<typeof setInterval> | null = null;
  let reconcileRate = 0;

  let boundUserId: number | null = null;
  let monitorBound = false;
  let dialogHandle: { hide: () => void } | null = null;

  const isFresh = (session: PresenceClient): boolean => now.value - new Date(session.last_seen_at).getTime() < staleAfter.value * 1000;

  /**
   * Only clients that have checked in recently enough to count. The server
   * filters the same way; this repeats it locally so a session that stopped
   * checking in between reconciliations is retired on time rather than sitting
   * in the list until the next poll.
   */
  const onlineUsers = computed<PresenceUser[]>(() =>
    users.value
      .map(user => ({ ...user, sessions: user.sessions.filter(isFresh) }))
      .filter(user => user.sessions.length > 0)
      .sort((a, b) => {
        const outdated = Number(isOutdated(b)) - Number(isOutdated(a));
        return outdated !== 0 ? outdated : lastSeen(b) - lastSeen(a);
      })
  );

  const onlineCount = computed(() => onlineUsers.value.length);
  const outdatedUsers = computed(() => onlineUsers.value.filter(isOutdated));
  const outdatedCount = computed(() => outdatedUsers.value.length);
  const sessionCount = computed(() => onlineUsers.value.reduce((total, user) => total + user.sessions.length, 0));

  function lastSeen(user: PresenceUser): number {
    return Math.max(...user.sessions.map(session => new Date(session.last_seen_at).getTime()));
  }

  /**
   * A version nobody published yet cannot make anyone late — without this the
   * whole company reads as outdated on a fresh install.
   */
  function isOutdated(user: PresenceUser): boolean {
    return serverVersion.value !== null && user.sessions.some(session => session.app_version !== serverVersion.value);
  }

  const setServerInfo = (info: Partial<FleetResponse>): void => {
    if (info.server_version !== undefined) {
      serverVersion.value = info.server_version;
    }
    if (info.stale_after) {
      staleAfter.value = info.stale_after;
    }
  };

  /**
   * One call sets both cadences, because they are two halves of the same thing:
   * the clock decides when a session looks stale, the reconciler makes sure it
   * only looks stale when it really is.
   */
  const runClock = (rate: number, reconcileRateMs: number): void => {
    if (clock === null || clockRate !== rate) {
      if (clock !== null) {
        clearInterval(clock);
      }
      clockRate = rate;
      clock = setInterval(() => (now.value = Date.now()), rate);
    }

    if (reconciler === null || reconcileRate !== reconcileRateMs) {
      if (reconciler !== null) {
        clearInterval(reconciler);
      }
      reconcileRate = reconcileRateMs;
      reconciler = setInterval(() => void fetchFleet({ silent: true }), reconcileRateMs);
    }
  };

  const stopClock = (): void => {
    if (clock !== null) {
      clearInterval(clock);
      clock = null;
      clockRate = 0;
    }
    if (reconciler !== null) {
      clearInterval(reconciler);
      reconciler = null;
      reconcileRate = 0;
    }
  };

  /**
   * `silent` is for the reconciler: a poll that flips `loading` would flash the
   * panel's skeleton over a list that is already on screen and correct.
   */
  const fetchFleet = async ({ silent = false } = {}): Promise<void> => {
    if (!UserStore.isDirecteur) {
      return;
    }

    if (!silent) {
      loading.value = true;
    }

    try {
      const { data } = await axiosApi.get<FleetResponse>('online');
      setServerInfo(data);
      users.value = data.users;
      loadFailed.value = false;
    } catch {
      // A failed poll is not a failed panel: keep showing the list we have and
      // let the next one correct it. Only a foreground load reports the error.
      if (!silent) {
        loadFailed.value = true;
      }
    } finally {
      loading.value = false;
    }
  };

  /**
   * Patches one person in place. A departure arrives as a null user, which is
   * also what a broadcast looks like when somebody's last tab closed.
   */
  const applyChange = (userId: number, user: PresenceUser | null): void => {
    const rest = users.value.filter(existing => existing.id !== userId);
    users.value = user ? [...rest, user] : rest;
  };

  /**
   * An operator reached into this client. Each action is the remote twin of a
   * button in the settings drawer, so a director stops having to talk somebody
   * through pressing it.
   */
  const applyCommand = (action: ClientCommand): void => {
    switch (action) {
      case 'update':
        void UpdateStore.installUpdateWithCountdown('Mise à jour forcée par un administrateur');
        return;

      case 'refresh-data':
        // `true` makes it announce itself: the account may look identical from
        // here, and silence would read as nothing having happened.
        void UserStore.regenUserData(true);
        return;

      case 'logout':
        showWarningNotification('Un administrateur a fermé votre session.');
        UserStore.logout(true);
        return;

      case 'reset':
        showWarningNotification("Un administrateur a réinitialisé l'application sur cet appareil.");
        void UpdateStore.resetAndSignOut();
        return;

      default:
        void UpdateStore.checkForUpdates({ notification: false });
    }
  };

  /**
   * Binds every listener this client needs. Idempotent and identity-aware: it
   * is called from more than one place and survives a login without a reload,
   * where the previous incarnation stacked a second set of listeners on the
   * same channels and double-counted everyone.
   */
  const init = (): void => {
    const userId = UserStore.details?.id;
    if (!userId) {
      return;
    }

    const socket = getSocket();
    if (!socket) {
      return;
    }

    if (boundUserId !== null && boundUserId !== userId) {
      socket.leave(`App.User.${boundUserId}`);
    }

    if (boundUserId !== userId) {
      boundUserId = userId;
      socket.private(`App.User.${userId}`).listen('.app.command', (payload: { action: ClientCommand }) => applyCommand(payload.action));
    }

    // Joined by everybody: it carries the announcements meant for the whole
    // company, and rejoining an already-joined channel returns the same one.
    socket
      .join('online')
      .listen('.app.command', (payload: { action: ClientCommand }) => applyCommand(payload.action))
      .listen('.App\\Events\\broadcastAppUpdate', (payload: { version: string }) => {
        serverVersion.value = payload.version;
        // Announce it rather than install it: a deploy should not yank the page
        // out from under whoever is mid-form. `UpdateHandler` takes it from
        // here, and a director can still force it on stragglers.
        void UpdateStore.checkForUpdates({ notification: false });
      });

    if (!UserStore.isDirecteur || monitorBound) {
      return;
    }

    monitorBound = true;
    socket.private('presence-monitor').listen('.presence.changed', (payload: { user_id: number; user: PresenceUser | null }) => {
      applyChange(payload.user_id, payload.user);
    });

    // The panel is patched by broadcasts, but a director who was not connected
    // when they happened needs a starting point.
    void fetchFleet();
    runClock(CLOCK_IDLE_MS, RECONCILE_IDLE_MS);
  };

  /**
   * The signed-in user's own private channel, for the features that piggyback
   * on it (messages, conversations). Echo hands back the same channel instance
   * for a name it already holds, so callers can ask for it whenever they need
   * it — which replaces the cached handle they used to read, and which was null
   * for anything that mounted before this store had connected.
   */
  const userChannel = () => {
    const userId = UserStore.details?.id;
    const socket = getSocket();
    return userId && socket ? socket.private(`App.User.${userId}`) : null;
  };

  const command = async (action: ClientCommand, userId: number | null = null): Promise<void> => {
    try {
      await axiosApi.post('online/command', { action, user_id: userId });
      showSuccessNotification(userId === null ? 'Commande envoyée à tous les appareils connectés.' : 'Commande envoyée.');
    } catch {
      showErrorNotification("La commande n'a pas pu être envoyée.");
    }
  };

  /** Force the update on everyone running something other than the published version. */
  const updateStragglers = async (): Promise<void> => {
    const targets = outdatedUsers.value.map(user => user.id);
    await Promise.all(targets.map(id => command('update', id)));
  };

  const republishVersion = async (): Promise<void> => {
    try {
      const { data } = await axiosApi.post<{ server_version: string | null; message: string }>('online/republish-version');
      setServerInfo(data);
      showSuccessNotification(`Version ${data.server_version ?? '?'} rediffusée.`);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showErrorNotification(message || "La version n'a pas pu être rediffusée.");
    }
  };

  const openDialog = (): void => {
    if (!UserStore.isDirecteur) {
      return;
    }

    if (dialogHandle) {
      dialogHandle.hide();
      return;
    }

    dialogOpen.value = true;
    runClock(CLOCK_OPEN_MS, RECONCILE_OPEN_MS);
    // Reconciles against the server on every open: broadcasts can be missed
    // while the socket is down, and reopening the panel is the natural moment
    // to stop trusting a patched list.
    void fetchFleet();

    dialogHandle = Dialog.create({
      component: defineAsyncComponent(() => import('src/components/dialogs/onlineUsersDialog.vue'))
    }).onDismiss(() => {
      dialogHandle = null;
      dialogOpen.value = false;
      runClock(CLOCK_IDLE_MS, RECONCILE_IDLE_MS);
    });
  };

  return {
    users,
    onlineUsers,
    onlineCount,
    sessionCount,
    outdatedUsers,
    outdatedCount,
    serverVersion,
    staleAfter,
    loading,
    loadFailed,
    dialogOpen,
    now,
    isOutdated,
    isFresh,
    setServerInfo,
    init,
    userChannel,
    fetchFleet,
    command,
    updateStragglers,
    republishVersion,
    openDialog,
    stopClock
  };
});
